/**
 * 경력을 문자열로 받아 숫자 타입으로 변환해준다.
 * 음수인 경우 0으로 포맷한다.
 * @param {string} value - 경력을 나타내는 문자열 값.
 * @returns {number | null} - 숫자 타입의 경력 값 또는 빈 문자열인 경우 null.
 */
export const formatCareerYear = (value: string): number | null => {
  if (value === "") return null;
  const parsedValue = parseInt(value);
  if (isNaN(parsedValue)) return null;
  return Math.max(0, Math.min(100, parsedValue));
};

/**
 * 연락처 문자열을 `XXX-XXXX-XXXX` 형식으로 포맷해준다.
 * 포맷 전에 숫자가 아닌 문자는 제거된다.
 * @param {string} value - 연락처를 나타내는 문자열.
 * @returns {string} - 포맷된 연락처 문자열.
 */
export const formatContactNumber = (value: string): string => {
  return value
    .replace(/[^0-9]/g, "")
    .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, "$1-$2-$3")
    .replace(/(\-{1,2})$/g, "");
};

/**
 * 문자열이 최대 길이를 초과할 경우, 지정된 길이로 자르고 '...'을 추가하여 반환한다.
 * @param {string} input - 원본 문자열.
 * @param {number} maxLength - 최대 문자열 길이.
 * @returns {string} - 잘린 문자열 또는 원본 문자열.
 */
export const truncateString = (input: string, maxLength: number): string => {
  return input.length > maxLength ? input.slice(0, maxLength) + "..." : input;
};

/**
 * 문자열에서 영어, 숫자, 한글을 제외한 모든 문자를 제거하고 공백을 하이픈(-)으로 변환한다.
 * @param {string} input - URL에 사용될 문자열.
 * @returns {string} - 포맷된 URL 문자열.
 */
export const formatUrl = (input: string): string => {
  return input.replace(/[^a-zA-Z0-9가-힣\s]/g, "").replace(/\s+/g, "-");
};

/**
 * HTML 문자열에서 <p>, <h1> ~ <h6> 태그 안의 텍스트 내용만 추출하여 반환한다.
 * @param {string} html - HTML 문자열.
 * @returns {string} - 추출된 내용 문자열.
 */
export const extractContent = (html: string): string => {
  const tagContentMatches = html.match(/<(p|h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/g);

  return tagContentMatches
    ? tagContentMatches
        .map((tag) => {
          return tag
            .replace(/<\/?[^>]+>/g, "")
            .replace(/\s+/g, " ")
            .trim();
        })
        .join(" ")
    : "";
};
